import { defineStore } from 'pinia';
import type {
    QlcWidget,
    AnimationMatrixState,
    SpeedDialState,
    ConnectionState,
} from '~~/shared/types/qlc';

export const useQlcStore = defineStore('qlc', () => {
    const widgets = ref<Record<string, QlcWidget>>({});
    const animationStates = ref<Record<string, AnimationMatrixState>>({});
    const speedDialStates = ref<Record<string, SpeedDialState>>({});

    const config = useRuntimeConfig();
    const serverAppMode = ((config.public.appMode as string) || '').toLowerCase() === 'cloud' ? 'cloud' : 'pi';

    const connection = ref<ConnectionState>({
        connected: false,
        mode: serverAppMode,
        pairingCode: null,
        statusText: serverAppMode === 'cloud'
            ? 'Verbinden met Cloud Server...'
            : 'Verbinden met QLC+...',
        lastMessageAt: 0,
        reconnectAttempts: 0,
    });

    const isMockMode = ref(false);

    function initMockData(): void {
        // Mock data intentionally disabled - only real QLC+ widgets supported
    }

    // Set or update a single widget
    function setWidget(widget: QlcWidget): void {
        widgets.value = {
            ...widgets.value,
            [widget.id]: {
                ...widgets.value[widget.id],
                ...widget,
            },
        };
    }

    const functionToWidgets = ref<Record<string, string[]>>({});
    const functionStates = ref<Record<string, boolean>>({});

    // Update live value for a widget
    function setWidgetValue(widgetId: string, value: number): void {
        const existing = widgets.value[widgetId];
        const isSpeedDial = existing?.type === 'SpeedDial';
        const clamped = isSpeedDial
            ? Math.max(0, Math.round(value))
            : Math.max(0, Math.min(255, Math.round(value)));
        if (existing) {
            existing.value = clamped;
        }
        else {
            widgets.value = {
                ...widgets.value,
                [widgetId]: {
                    id: widgetId,
                    name: `Widget ${widgetId}`,
                    type: 'Button',
                    value: clamped,
                },
            };
        }
    }

    // Associate a widget with its QLC+ function ID
    function setWidgetFunction(widgetId: string, functionId: string): void {
        const existing = widgets.value[widgetId];
        if (existing) {
            existing.functionId = functionId;
        }
        if (!functionToWidgets.value[functionId]) {
            functionToWidgets.value[functionId] = [];
        }
        if (!functionToWidgets.value[functionId].includes(widgetId)) {
            functionToWidgets.value[functionId].push(widgetId);
        }

        // If we already received a function status update for this function, apply it now
        if (functionStates.value[functionId] !== undefined) {
            setWidgetValue(widgetId, functionStates.value[functionId] ? 255 : 0);
        }
    }

    // Handle FUNCTION|<id>|Running or FUNCTION|<id>|Stopped events from QLC+
    function handleFunctionState(functionId: string, isRunning: boolean): void {
        const value = isRunning ? 255 : 0;
        functionStates.value[functionId] = isRunning;

        // 1. Update any widgets mapped to this function ID
        const mappedWidgetIds = functionToWidgets.value[functionId] || [];
        for (const wId of mappedWidgetIds) {
            setWidgetValue(wId, value);
        }

        // 2. Also check if any widget has functionId property set
        for (const widget of Object.values(widgets.value)) {
            if (widget.functionId === functionId && !mappedWidgetIds.includes(widget.id)) {
                setWidgetValue(widget.id, value);
            }
        }
    }

    // Update animation matrix parameters
    function setAnimationState(widgetId: string, update: Partial<AnimationMatrixState>): void {
        const current = animationStates.value[widgetId] || {
            widgetId,
            colors: ['#00f0ff', '#ff0077', '#ffff00', '#00ff00', '#9900ff'],
            primaryColor: '#00f0ff',
            secondaryColor: '#ff0077',
            speed: 100,
            presets: [],
        };

        animationStates.value[widgetId] = {
            ...current,
            ...update,
        };
    }

    // Update connection status
    function setConnectionStatus(status: Partial<ConnectionState>): void {
        connection.value = {
            ...connection.value,
            ...status,
        };
    }

    // Toggle mock mode
    function toggleMockMode(enabled?: boolean): void {
        isMockMode.value = enabled !== undefined ? enabled : !isMockMode.value;
        if (isMockMode.value) {
            initMockData();
            setConnectionStatus({
                connected: true,
                mode: 'mock',
                statusText: 'Mock Modus Actief (Geen QLC+ vereist)',
                lastMessageAt: Date.now(),
            });
        }
        else {
            setConnectionStatus({
                connected: false,
                mode: serverAppMode,
                statusText: serverAppMode === 'cloud'
                    ? 'Verbinden met Cloud Server...'
                    : 'Verbinden met echte QLC+...',
            });
        }
    }

    // Update speed dial state (factor, timeMs, and bpm)
    function setSpeedDialState(widgetId: string, update: Partial<SpeedDialState>): void {
        const current = speedDialStates.value[widgetId] || {
            widgetId,
            factorIndex: 6,
            timeMs: 500,
            bpm: 120,
        };

        const nextBpm = update.bpm ?? (update.timeMs ? Math.round(60000 / update.timeMs) : current.bpm);
        const nextTimeMs = update.timeMs ?? (update.bpm ? Math.round(60000 / update.bpm) : current.timeMs);

        speedDialStates.value[widgetId] = {
            ...current,
            ...update,
            bpm: nextBpm,
            timeMs: nextTimeMs,
        };

        if (nextTimeMs !== undefined) {
            setWidgetValue(widgetId, nextTimeMs);
        }
    }

    // Get widget by ID (with fallback)
    function getWidget(widgetId: string): QlcWidget | undefined {
        return widgets.value[widgetId];
    }

    return {
        widgets,
        animationStates,
        speedDialStates,
        connection,
        isMockMode,
        initMockData,
        setWidget,
        setWidgetValue,
        functionToWidgets,
        functionStates,
        setWidgetFunction,
        handleFunctionState,
        setAnimationState,
        setSpeedDialState,
        setConnectionStatus,
        toggleMockMode,
        getWidget,
    };
});
